import { useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { Link } from "@tanstack/react-router";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import type { PersonDetail } from "@/api/db/people";
import type { SaveResult } from "@/api/db/person-write";
import { Button } from "@/components/ui/button";
import { useSavePerson } from "@/hooks/use-save-person";
import {
	CadastroFormSchemas,
	displayDraftFromPerson,
	draftToInput,
	emptyDraft,
} from "@/lib/cadastro-draft";

import { ClinicSection } from "./clinic-section";
import { ConsentsSection } from "./consents-section";
import { ContactSection } from "./contact-section";
import { IdentitySection } from "./identity-section";
import { SaveErrorFeedback, SaveFeedback } from "./save-feedback";

export function CadastroForm({ person }: { person?: PersonDetail }) {
	const editingId = person?.id;
	const methods = useForm<typeof CadastroFormSchemas.draft.infer>({
		resolver: arktypeResolver(CadastroFormSchemas.draft),
		mode: "onChange",
		defaultValues:
			person === undefined
				? { ...emptyDraft, phones: [""], emails: [""] }
				: displayDraftFromPerson(person),
	});
	const { control, handleSubmit, reset } = methods;
	const [phones, emails] = useWatch({ control, name: ["phones", "emails"] });
	const [result, setResult] = useState<SaveResult | null>(null);
	const { save, isPending, isError } = useSavePerson(setResult);
	const submit = handleSubmit((draft) => {
		setResult(null);
		save(draftToInput(draft, editingId));
	});

	const handleNewEntry = () => {
		reset({ ...emptyDraft, phones: [""], emails: [""] });
		setResult(null);
	};

	const submitLabel = editingId === undefined ? "Cadastrar pessoa" : "Salvar cadastro";
	const pendingLabel = editingId === undefined ? "Cadastrando pessoa..." : "Salvando cadastro...";
	const actionLabel = isPending ? pendingLabel : submitLabel;

	return (
		<FormProvider {...methods}>
			<form
				data-component="cadastro-form"
				data-phone-count={phones.length}
				data-email-count={emails.length}
				onSubmit={submit}
				className="flex flex-col gap-6"
			>
				{!!result && <SaveFeedback result={result} />}
				{isError && <SaveErrorFeedback />}

				<IdentitySection />
				<ContactSection />
				<ClinicSection />
				<ConsentsSection />

				<div className="flex flex-wrap items-center justify-end gap-3">
					{!!result && (
						<Button asChild variant="outline" data-slot="view-ficha">
							<Link to="/pessoa/$id" params={{ id: result.person_id }}>
								Ver ficha
							</Link>
						</Button>
					)}

					{!!result && editingId === undefined && (
						<Button type="button" variant="ghost" data-slot="new-entry" onClick={handleNewEntry}>
							Novo cadastro
						</Button>
					)}

					<Button
						type="submit"
						disabled={isPending || !methods.formState.isValid}
						data-slot="submit"
					>
						{actionLabel}
					</Button>
				</div>
			</form>
		</FormProvider>
	);
}
